import {
    Grid,
    Button,
    IconButton,
    Box,
    Stack,
    Typography
} from "@mui/material";
import React, { useMemo } from "react";
import { Column } from "react-table";
import { useState, useEffect } from "react";
import { apiStore } from "../../../api/ApiFactory";
import { useNavigate } from "react-router-dom";
import { formatDateToMMDDYYYY } from "../../../helpers/utils/formatDate";
import MainModal from "../../../components/modal/MainModal";
import ConfirmationBox from "../../../components/modal/ConfirmationBox";
import { useAuth } from "../../../store/hooks/useAuth";
import { GetAuthStatus } from "../../../services/AuthService";
import { Permissions, Roles } from "../../../helpers/enums/AuthConstants";
import { useSelector } from "react-redux";
import { getAllDealerLocations } from "../../../store/slices/Dealer/DealerSelector";
import { getClaimStatusFilterOptions } from "../../../store/slices/WarrantyClaim/WarrantyClaimSelector";
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from '@mui/icons-material/Visibility';
import { IClaimHistory } from "../../../models/WarrantyClaimManagement";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNotification } from "../../../store/context/NotificationContext";
import { Table } from "../../../components/table/Table";
import { ServerSelectColumnFilter } from "../../../components/table/filters/ServerSelectColumnFilter";

const ClaimManagement = () => {
    const navigate = useNavigate();
    const [claimNumber, setClaimNumber] = useState<number>(-1);
    const [deleteClaimModal, setDeleteClaimModal] = useState(false);
    const openDeleteClaimModal = () => setDeleteClaimModal(true);
    const closeDeleteClaimModal = () => setDeleteClaimModal(false);
    const { Toast } = useNotification();
    let authuser = useAuth().authuser;
    const [userRoleAndPermission, setUserRoleAndPermission] = useState({ role: authuser?.user.role, editWarrantyClaim: GetAuthStatus([Permissions.EditWarrantyClaim]).isAuthorized , viewWarrantyClaim: GetAuthStatus([Permissions.ViewWarrantyClaim]).isAuthorized });
    const [disableControls, setDisableControls] = useState(false);
    const claimStatusFilterOptions = useSelector(getClaimStatusFilterOptions);
    const dealerLocations = useSelector(getAllDealerLocations);

    //Calls a Callback function in Table.tsx to force a refresh while preserving state information.
    const [forceRefresh, setForceRefresh] = useState<any>();

    const handleDeleteClaimBtnClick = (row: any, refreshHandler: any) => {

        setClaimNumber(row.claimNumber);
        setForceRefresh({refreshHandler}); //This is necessary to update the list page and maintain existing filters/sort/page.
        openDeleteClaimModal();    
    };

    const handleClaimCreate = () => {
        navigate(`/warranty/claims/create`);
    }

    const handleClaimEdit = (row : any) => {
        navigate(`/warranty/claims/edit/${row.claimNumber}`);
    }  

    const handleClaimView = (row: any) => {
        navigate(`/warranty/claims/view/${row.claimNumber}`);
    }   

    const deleteClaimOnConfirm = async () => {
        try {
            setDisableControls(true);
            closeDeleteClaimModal();
            await apiStore.warrantyclaimApi.deleteWarrantyClaim(claimNumber);

            if(forceRefresh !== undefined) {
                forceRefresh.refreshHandler(); 
            }

            Toast.success({ title: `Claim deleted successfully.` });

        } catch (error) {
            if (error instanceof Error) {
                Toast.error({ title: error.message });
            } else {
                Toast.error({ title: 'An unknown error occurred during deletion.' });
            }
        } finally {
            setDisableControls(false);            
        }       
    };

    const getColumnDefintion =  () => {

        let columns: Column<IClaimHistory>[] = [
            {
                Header: "Actions",
                accessor: "action",
                disableFilters: true,
                Cell: ({ row, forceRefreshHandler }: any) => {

                    const { claimStatus } = row.original;
                    return (   
                        <>
                            {userRoleAndPermission.viewWarrantyClaim && !userRoleAndPermission.editWarrantyClaim && (
                                <>
                                    <IconButton title="View"  onClick={() => handleClaimView(row.original)}>
                                        <ViewIcon style={{ marginRight: 2 }} />
                                    </IconButton>
                                </>
                            )}
                            {userRoleAndPermission.viewWarrantyClaim && userRoleAndPermission.editWarrantyClaim && (  
                                <>
                                    {claimStatus === "ACC" && (
                                        <IconButton title="View" onClick={() => handleClaimView(row.original)}>
                                            <ViewIcon style={{ marginRight: 2 }} />
                                        </IconButton>
                                    )}

                                    {claimStatus === "INI" && (
                                        <>
                                        <IconButton title="Edit" onClick={() => handleClaimEdit(row.original)}>
                                        <EditIcon style={{ marginRight: 2 }} />
                                        </IconButton>

                                        <IconButton title="Delete" onClick={() => handleDeleteClaimBtnClick(row.original, forceRefreshHandler)}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                        </>
                                    )}                                        
                                    {claimStatus !== "INI" && claimStatus !== "ACC" && (
                                        <IconButton title="View" onClick={() => handleClaimView(row.original)}>
                                            <ViewIcon style={{ marginRight: 2 }} />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </>
                    );
                },
            },
            {
                Header: "Claim Number",
                accessor: "claimNumber",
                width: 120,
            },
            {
                Header: "Auth Number",
                accessor: "authorizationNumber",
                width: 120,
            },
            {
                Header: "HIN",
                accessor: "hin",
            },
            {
                Header: "Model",
                accessor: "model",
            },
            {
                Header: "Year",
                accessor: "modelYear",
            },
            {

                Header: "Entry Date",
                accessor: "submitDate",
                width: 150,
                Cell: (cell: any) => (cell.value ? formatDateToMMDDYYYY(cell.value) : ""),
            },
            {
                Header: "Date of Failure",
                accessor: "failureDate",
                width: 150,
                Cell: (cell: any) => (cell.value ? formatDateToMMDDYYYY(cell.value) : ""),
            },
            {
                Header: "Status",
                accessor: "claimStatusDescription",
                Filter: ServerSelectColumnFilter,
                filterOptions: claimStatusFilterOptions
            }
        ];

        if(userRoleAndPermission.role !== Roles.DEALER) {
            columns.push({
                Header: "Dealer",
                accessor: "customerName",
                Filter:ServerSelectColumnFilter,
                filterOptions: dealerLocations.map((item) => {return {name: item.name, value: item.customerID}})
            });
        }

        return columns;
    }

    //Table Columns Structure
    const columns = useMemo(
        () => getColumnDefintion(),
        [claimStatusFilterOptions]
    );

    if(claimStatusFilterOptions === undefined) {
        return <></>
    }

    return (
        <>            
            <MainModal open={deleteClaimModal} handleClose={closeDeleteClaimModal}>
                <ConfirmationBox
                    confirmTitle="Proceed"
                    cancelTitle="Close"
                    cancelClick={closeDeleteClaimModal}
                    confirmClick={() => deleteClaimOnConfirm()}
                    disable={disableControls}
                    message={`Record will be permanently removed from the system. Are you sure you want to delete warranty claim number ${claimNumber}?`}
                />
            </MainModal> 

            <Stack
                className="header-label-container"
                direction="row"
                justifyContent="space-between"
            >
                <Typography variant="h5">{"Claims"}</Typography>
                
                {userRoleAndPermission.editWarrantyClaim && userRoleAndPermission.role === Roles.ADMINISTRATOR && (
                    <Button variant="contained"  color="primary" onClick={handleClaimCreate}>
                        Create New Claim
                    </Button>
                )}
            </Stack>

            <Grid mt={2}>

                <Table<IClaimHistory>
                    className="show-action-column"
                    name="claim-table"
                    columns={columns}
                    serverOnChange={true}
                    apiGet="/warrantyclaim/GetList"
                    inlineLockState={false}
                    initialFilters={[{id: "claimStatusDescription", value: "NC"}]}
                />
               
            </Grid>
        </>
    );
};
export default ClaimManagement;
